"use client";

import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetAllPizzaTypes } from "@/api/pizza-checker/pizza-types/pizza-types";
import { PizzaType } from "@/api/custom-schema";
import { columns } from "./columns";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";



/** Names shown in the column picker for the columns a user can hide. */
const COLUMN_LABEL_KEYS: Record<string, TranslationKey> = {
  id: "pizzaTypes.columns.id",
  name: "common.name",
  components: "pizzaTypes.columns.ingredients",
  component_count: "pizzaTypes.columns.ingredientCount",
};

type PizzaTypesProps = {
  getLink: (pizzaTypeId: string) => string;
};

const PIZZA_TYPE_NAME_FILTER_STORAGE_KEY =
  "pizza-admin:pizza-type-name-filter:v1";
const PIZZA_TYPE_COMPONENT_FILTER_STORAGE_KEY =
  "pizza-admin:pizza-type-component-filter:v1";

export default function PizzaTypes({ getLink }: PizzaTypesProps) {
  const { t, locale } = useLocalization();
  const { data: pizzaTypes, isLoading, error, refetch } = useGetAllPizzaTypes<PizzaType[]>();
  
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    id: true,
    name: true,
    components: true,
    component_count: true,
    detail: true,
    actions: true,
  });
  const [rowSelection, setRowSelection] = React.useState({});
  const [nameFilter, setNameFilter] = useLocalStorageState(
    PIZZA_TYPE_NAME_FILTER_STORAGE_KEY,
    "",
  );
  const [componentFilter, setComponentFilter] = useLocalStorageState(
    PIZZA_TYPE_COMPONENT_FILTER_STORAGE_KEY,
    "",
  );
  
  // Store pagination state
  const [paginationState, setPaginationState] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Filter pizza types based on name and components
  const filteredPizzaTypes = React.useMemo(() => {
    if (!pizzaTypes) return [];
    
    let filtered = pizzaTypes;
    
    // Filter by name
    if (nameFilter.trim()) {
      filtered = filtered.filter(pizzaType =>
        pizzaType.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
        pizzaType.id.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }
    
    // Filter by components
    if (componentFilter.trim()) {
      filtered = filtered.filter(pizzaType =>
        pizzaType.components.some(component =>
          component.name.toLowerCase().includes(componentFilter.toLowerCase())
        )
      );
    }
    
    return filtered;
  }, [pizzaTypes, nameFilter, componentFilter]);



  // Memoize columns to prevent unnecessary re-renders (the locale keeps their texts current)
  const memoizedColumns = React.useMemo(
    () => columns(getLink),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getLink, locale]
  );

  const table = useReactTable({
    data: filteredPizzaTypes,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPaginationState,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: paginationState,
    },
    initialState: {
      pagination: paginationState,
    },
    autoResetPageIndex: false,
    autoResetExpanded: false,
    getRowId: (row) => row.id,
  });

  if (isLoading) {
    return (
      <div className="w-full gap-4 flex flex-col">
        <div className="flex items-center justify-center h-32">
          <div className="text-sm text-muted-foreground">{t("pizzaTypes.list.loading")}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full gap-4 flex flex-col">
        <div className="flex items-center justify-center h-32">
          <div className="text-sm text-destructive">
            {t("pizzaTypes.list.loadFailed")}
            <Button variant="link" onClick={() => refetch()} className="p-0 ml-1">
              {t("pizzaTypes.list.retry")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full gap-4 flex flex-col">
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("pizzaTypes.list.nameFilter")}
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          className="max-w-sm"
        />
        <Input
          placeholder={t("pizzaTypes.list.ingredientFilter")}
          value={componentFilter}
          onChange={(event) => setComponentFilter(event.target.value)}
          className="max-w-sm"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              {t("common.columns")} <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {COLUMN_LABEL_KEYS[column.id]
                      ? t(COLUMN_LABEL_KEYS[column.id])
                      : column.id.replace('_', ' ')}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {t("pizzaTypes.list.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">{t("common.perPage")}</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex min-w-[100px] items-center justify-center whitespace-nowrap px-2 text-sm font-medium">
            {t("common.page", {
              page: table.getState().pagination.pageIndex + 1,
              pages: table.getPageCount(),
            })}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("common.firstPage")}</span>
              <ChevronsLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("common.previousPage")}</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("common.nextPage")}</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("common.lastPage")}</span>
              <ChevronsRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
